import { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { Button } from "./Button";

const SIZE_CLASS = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
  "2xl": "max-w-3xl",
} as const;

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal dialog primitive. Beyond the chrome it owns the a11y mechanics every
 * dialog needs: body scroll-lock, focus moved into the panel on open (first
 * field, else first focusable), Tab cycling trapped inside, focus returned
 * to the opener on close, Escape/backdrop close. `footer` renders a fixed
 * action bar under the (scrollable) body so long forms never bury their
 * buttons.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  footerClassName,
  size = "md",
  placement = "center",
  className,
  role = "dialog",
  closeLabel,
  dismissible = true,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Optional one-liner under the title — what this dialog does. */
  description?: string;
  children: ReactNode;
  /** Optional fixed action bar; content scrolls independently above it. */
  footer?: ReactNode;
  /** Replaces the footer bar's alignment/background (default: right-aligned
   * on the surface colour) — e.g. a tinted bar with content on both sides. */
  footerClassName?: string;
  size?: keyof typeof SIZE_CLASS;
  /** `right`: a full-height side panel (edit forms beside the content). */
  placement?: "center" | "right";
  className?: string;
  /** `alertdialog` for confirmations that interrupt (e.g. losing unsaved changes). */
  role?: "dialog" | "alertdialog";
  /** Accessible name of the ✕ button (default: "Zurück"). */
  closeLabel?: string;
  /** `false`: Escape and a backdrop click do not close (only the ✕ and the
   * dialog's own buttons do) — for content that must not be lost by accident. */
  dismissible?: boolean;
}) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  // Read via ref inside the effect: the effect must run exactly once per
  // open/close. Depending on `onClose` re-runs it whenever a parent re-render
  // passes a fresh closure (draft autosave, query update, …) — its cleanup
  // then bounces focus to the opener and back to the FIRST field, stealing
  // the caret mid-typing.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dismissibleRef = useRef(dismissible);
  dismissibleRef.current = dismissible;

  useEffect(() => {
    if (!open) return;
    openerRef.current = document.activeElement as HTMLElement | null;

    // Move focus to the first form control (else first focusable) so typing
    // can start immediately; the close button is deliberately skipped.
    const raf = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel || panel.contains(document.activeElement)) return;
      const preferred = panel.querySelector<HTMLElement>(
        "input, textarea, select, [data-autofocus]",
      );
      const target =
        preferred ??
        Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).find(
          (el) => !el.hasAttribute("data-dialog-close"),
        );
      target?.focus();
    });

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (dismissibleRef.current) onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !panel.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !panel.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      openerRef.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex",
        placement === "right" ? "items-stretch justify-end" : "items-center justify-center p-4",
      )}
      role={role}
      aria-modal="true"
      aria-labelledby="dialog-title"
    >
      <button
        type="button"
        tabIndex={-1}
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px] motion-safe:animate-[dialog-fade_120ms_ease-out]"
        aria-label={t("common.back")}
        onClick={dismissible ? onClose : undefined}
      />
      <div
        ref={panelRef}
        className={cn(
          "relative z-10 flex w-full flex-col border-hairline bg-surface shadow-xl",
          placement === "right"
            ? "h-full border-l"
            : "max-h-[calc(100vh-3rem)] rounded-lg border motion-safe:animate-[dialog-pop_140ms_ease-out]",
          SIZE_CLASS[size],
          className,
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b border-hairline px-4 py-3">
          <div className="min-w-0">
            <h2 id="dialog-title" className="font-display text-sm font-semibold text-ink">
              {title}
            </h2>
            {description && <p className="mt-0.5 text-xs text-muted">{description}</p>}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label={closeLabel ?? t("common.back")}
            data-dialog-close
            className="-mr-1 shrink-0"
          >
            ✕
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 text-sm text-ink">{children}</div>
        {footer && (
          <div
            className={cn(
              "flex items-center gap-2 border-t border-hairline px-4 py-3",
              footerClassName ?? "justify-end bg-surface",
            )}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
